/**
 * 测试报告步骤执行详情组件（正式报告页与只读分享页共用）
 * - StepResultDetail：接口场景步骤（响应状态/耗时、响应头、响应内容、断言结果、参数提取）
 * - UIStepResultDetail：UI 步骤（执行结论、操作明细、AI 判定理由、执行截图）
 */
import type { TestReportStep } from '@/api/reports'

/** 代码区小标题样式（参考 code-section h5） */
const codeTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--au-slate-muted)',
  letterSpacing: '0.04em',
  marginBottom: 8,
}

/** 深色代码块样式（headers/body，两种模式下均为深色终端风） */
const darkCodeStyle: React.CSSProperties = {
  margin: 0,
  overflow: 'auto',
  padding: '12px 16px',
  borderRadius: 8,
  fontSize: 12,
  lineHeight: 1.7,
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-all' as const,
  background: '#2d3142',
  color: '#d6dbe8',
}

/** 解析步骤执行结果 JSON（接口测试与 UI 测试共用，字段按需取用） */
export const parseStepResult = (record: TestReportStep): {
  // 接口测试
  message?: string
  response?: { status?: number; elapsed_ms?: number; body?: string; headers?: Record<string, string> }
  assertions?: { id?: string; passed: boolean; message: string; type?: string; operator?: string; expected?: string; key?: string; expression?: string }[]
  extracts?: { name?: string; success: boolean; value?: string; message?: string }[]
  // UI 测试
  mode?: string
  element?: string
  locator?: string
  value?: string
  action?: Record<string, unknown>
  error?: string
  passed?: boolean
  confidenceScore?: number
  reasoning?: string
  screenshot_url?: string
} => {
  try {
    return JSON.parse(record.result || '{}')
  } catch {
    return { message: record.result }
  }
}

/** 断言类型中文文本 */
const assertionTypeText: Record<string, string> = {
  status_code: '状态码', response_header: '响应头', response_body: '响应体', response_time: '响应时间',
}

/** 把 AI 动作 JSON / 元素步骤结果转成详细中文描述 */
function describeUIAction(
  parsed: ReturnType<typeof parseStepResult>,
): { text: string; locator?: string } {
  // 元素库步骤：用元素名称 + 动作 + 输入值拼出明确描述
  if (parsed.mode === 'element') {
    const el = parsed.element || '未知元素'
    const act = String(parsed.action ?? 'click')
    if (act === 'input') return { text: `在元素「${el}」中输入「${parsed.value ?? ''}」`, locator: parsed.locator }
    if (act === 'hover') return { text: `悬停在元素「${el}」上`, locator: parsed.locator }
    return { text: `点击元素「${el}」`, locator: parsed.locator }
  }
  const a = parsed.action as Record<string, string | number | undefined> | undefined
  if (!a) return { text: parsed.message || '-' }
  const target = (a.description || a.selector || '') as string
  switch (String(a.type || '')) {
    case 'click':
      return { text: `点击「${target}」`, locator: a.selector as string | undefined }
    case 'input':
      return { text: `在输入框「${target}」中输入「${a.value ?? ''}」`, locator: a.selector as string | undefined }
    case 'navigate':
      return { text: `打开页面「${(a.url as string) || target}」` }
    case 'hover':
      return { text: `悬停在「${target}」上`, locator: a.selector as string | undefined }
    case 'press':
      return { text: `按下按键「${a.key ?? ''}」` }
    case 'wait':
      return { text: `等待 ${(a.ms ?? a.timeout ?? '')} 毫秒` }
    case 'scroll':
      return { text: `滚动页面至「${target || '目标位置'}」` }
    default:
      return { text: JSON.stringify(a, null, 2) }
  }
}

/** UI 测试步骤执行结果详情：执行结论 / AI 动作 / 断言判定 / 执行截图 */
export function UIStepResultDetail({ record, isDark }: { record: TestReportStep; isDark: boolean }) {
  const parsed = parseStepResult(record)
  const blockStyle = {
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    border: '1px solid var(--au-border)',
  }
  const isAssertion = record.step_type === 5 || record.step_type === 10
  const action = describeUIAction(parsed)
  return (
    <div
      style={{
        background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--au-header-bg)',
        border: '1px solid var(--au-border)',
        borderRadius: 12,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* 执行结论 */}
      <div>
        <div style={codeTitleStyle}>{isAssertion ? '判定结论' : '执行结论'}</div>
        {isAssertion ? (
          <div style={{ ...blockStyle, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, color: parsed.passed ? 'var(--au-primary)' : 'var(--au-terracotta)' }}>
              {parsed.passed ? '✓ 通过' : '✗ 未通过'}
            </span>
            {parsed.confidenceScore !== undefined && (
              <span style={{ color: 'var(--au-slate-muted)' }}>置信度 {parsed.confidenceScore}/100</span>
            )}
          </div>
        ) : (
          <div style={{ ...blockStyle, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, color: record.status === 1 ? 'var(--au-primary)' : 'var(--au-terracotta)' }}>
              {record.status === 1 ? '✓ 执行成功' : record.status === 4 ? '○ 已跳过' : '✗ 执行失败'}
            </span>
            {parsed.message && <span>{parsed.message}</span>}
          </div>
        )}
      </div>
      {/* 失败原因 */}
      {record.status !== 1 && record.status !== 4 && parsed.error && (
        <div>
          <div style={codeTitleStyle}>失败原因</div>
          <div style={{ ...blockStyle, color: 'var(--au-terracotta)' }}>{parsed.error}</div>
        </div>
      )}
      {/* 操作明细（点了哪个元素 / 输入了什么内容） */}
      {!isAssertion && action.text && action.text !== '-' && (
        <div>
          <div style={codeTitleStyle}>操作明细</div>
          <div style={blockStyle}>{action.text}</div>
          {action.locator && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--au-slate-muted)', wordBreak: 'break-all' }}>
              定位：{action.locator}
            </div>
          )}
        </div>
      )}
      {/* AI 判定理由 */}
      {parsed.reasoning && (
        <div>
          <div style={codeTitleStyle}>AI {isAssertion ? '判定' : ''}理由</div>
          <div style={blockStyle}>{parsed.reasoning}</div>
        </div>
      )}
      {/* AI 翻译出的原始动作 JSON */}
      {parsed.action && !isAssertion && parsed.mode !== 'element' && (
        <div>
          <div style={codeTitleStyle}>AI 动作（原始）</div>
          <pre style={{ ...darkCodeStyle, maxHeight: 220 }}>
            {(() => {
              try {
                return JSON.stringify(parsed.action, null, 2)
              } catch {
                return String(parsed.action)
              }
            })()}
          </pre>
        </div>
      )}
      {/* 执行截图（步骤失败截图 / 断言判定时刻截图） */}
      {parsed.screenshot_url && (
        <div>
          <div style={codeTitleStyle}>执行截图</div>
          <a href={parsed.screenshot_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block' }}>
            <img
              src={parsed.screenshot_url}
              alt="执行截图"
              style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, border: '1px solid var(--au-border)' }}
            />
          </a>
          <div style={{ marginTop: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>点击图片查看原图</span>
          </div>
        </div>
      )}
    </div>
  )
}

/** 步骤执行结果详情（展开行）：米色容器 + 深色代码块，参考 aurora-report.html 的 step-detail */
export function StepResultDetail({ record, isDark }: { record: TestReportStep; isDark: boolean }) {
  const parsed = parseStepResult(record)
  const response = parsed.response
  const assertions = parsed.assertions || []
  const extracts = parsed.extracts || []
  const blockStyle = {
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    border: '1px solid var(--au-border)',
  }
  // 按步骤类型决定展示内容：
  // HTTP：响应状态/耗时、响应头、响应内容、断言结果、参数提取结果
  // 等待/条件/循环/数据库/脚本：仅执行结果
  // 断言控制：执行结果 + 断言结果
  const isHttp = record.step_type === 1 && !!response
  const isAssertionCtrl = record.step_type === 5
  const showResultMessage = !isHttp
  const showAssertions = isHttp || isAssertionCtrl
  const showExtracts = isHttp
  return (
    <div
      style={{
        background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--au-header-bg)',
        border: '1px solid var(--au-border)',
        borderRadius: 12,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* HTTP 响应状态与耗时（头部 kv 行 + 分隔线） */}
      {isHttp && response && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            fontSize: 14,
            paddingBottom: 12,
            borderBottom: '1px solid var(--au-border)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ color: 'var(--au-slate-muted)' }}>响应状态</span>
            <span style={{ fontWeight: 800, color: (response.status || 0) < 400 ? 'var(--au-primary)' : 'var(--au-terracotta)' }}>
              {response.status ?? '-'}
            </span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ color: 'var(--au-slate-muted)' }}>响应耗时</span>
            <span style={{ fontWeight: 800, color: 'var(--au-slate)' }}>{response.elapsed_ms ?? '-'}ms</span>
          </span>
        </div>
      )}
      {/* 非HTTP步骤：执行结果 */}
      {showResultMessage && (
        <div>
          <div style={codeTitleStyle}>执行结果</div>
          <div style={blockStyle}>{parsed.message || '-'}</div>
        </div>
      )}
      {/* 响应头（深色终端风代码块） */}
      {isHttp && response?.headers && Object.keys(response.headers).length > 0 && (
        <div>
          <div style={codeTitleStyle}>响应头 Headers</div>
          <pre style={{ ...darkCodeStyle, maxHeight: 150, background: '#363a4d' }}>
            {(() => {
              try {
                return Object.entries(response.headers)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join('\n')
              } catch {
                return JSON.stringify(response.headers, null, 2)
              }
            })()}
          </pre>
        </div>
      )}
      {/* 响应内容（深色终端风代码块） */}
      {isHttp && response?.body && (
        <div>
          <div style={codeTitleStyle}>响应内容 Body</div>
          <pre style={{ ...darkCodeStyle, maxHeight: 300 }}>
            {(() => {
              try {
                return JSON.stringify(JSON.parse(response.body), null, 2)
              } catch {
                return response.body
              }
            })()}
          </pre>
        </div>
      )}
      {/* 断言结果 */}
      {showAssertions && (
        <div>
          <div style={codeTitleStyle}>断言结果</div>
        {assertions.length === 0 ? (
          <div style={{ ...blockStyle, color: 'var(--au-text-faint)' }}>无</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {assertions.map((a, idx) => (
              <div key={a.id || idx} style={{ ...blockStyle, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontWeight: 700, color: a.passed ? 'var(--au-primary)' : 'var(--au-terracotta)', flexShrink: 0 }}>{a.passed ? '✓' : '✗'}</span>
                <div>
                  <span style={{ color: isDark ? 'var(--text)' : 'var(--au-slate)' }}>
                    {a.type === 'response_body' && a.expression ? `表达式 "${a.expression}"：` : ''}
                    {a.type === 'response_header' && a.key ? `响应头 ${a.key}：` : ''}
                    {a.type ? `[${assertionTypeText[a.type] || a.type}] ` : ''}
                    {a.message}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
      {/* 参数提取结果 */}
      {showExtracts && (
        <div>
          <div style={codeTitleStyle}>参数提取结果</div>
          {extracts.length === 0 ? (
            <div style={{ ...blockStyle, color: 'var(--au-text-faint)' }}>无</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {extracts.map((ex, idx) => (
                <div key={ex.name || idx} style={{ ...blockStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, color: ex.success ? 'var(--au-primary)' : 'var(--au-terracotta)', flexShrink: 0 }}>{ex.success ? '✓' : '✗'}</span>
                  <span style={{ fontWeight: 600 }}>{ex.name || '(未命名变量)'}</span>
                  <span style={{ color: isDark ? 'var(--text-secondary, var(--au-text-faint))' : 'var(--au-slate-muted)' }}>
                    {ex.success ? `= ${ex.value}` : `提取失败：${ex.message || '-'}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
